import React from 'react';
import Box from '@mui/material/Box';
import { DataGrid, GridColDef, GridCellParams } from '@mui/x-data-grid';
import { useAdminCompanyPeriodsQuery } from '../../api/admin';
import AdminQuickFilterToolbar from './AdminQuickFilterToolbar';

interface Props {
	companyId: number;
}

const daysDiff = (a: string, b: string) =>
	Math.round((new Date(a).getTime() - new Date(b).getTime()) / 86400000);

export default function CompanyPeriodsTable({ companyId }: Props) {
	const { data: periods, isLoading } = useAdminCompanyPeriodsQuery(companyId);

	const rows = React.useMemo(() => {
		if (!periods) return [];
		const periodMap = new Map(periods.map((r) => [r.fs_period, r]));
		return periods.map((row, i) => {
			const delta_days = daysDiff(row.period_end, row.period_start);
			const gap = i > 0 ? daysDiff(row.period_start, periods[i - 1].period_end) - 1 : null;
			const yoyPeriod = row.fs_period.replace(/(\d{4})/, (y) => String(Number(y) - 1));
			const yoyRow = periodMap.get(yoyPeriod);
			const yoy_end_delta = yoyRow ? daysDiff(row.period_end, yoyRow.period_end) : null;
			return { ...row, delta_days, gap, yoy_end_delta };
		});
	}, [periods]);

	const columns: GridColDef[] = [
		{ field: 'symbol', headerName: 'Symbol', flex: 1 },
		{ field: 'fs_period', headerName: 'Period', flex: 1 },
		{
			field: 'period_start',
			headerName: 'Start',
			flex: 1,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{
			field: 'period_end',
			headerName: 'End',
			flex: 1,
			valueFormatter: ({ value }) =>
				value ? new Date(value).toLocaleDateString('en-CA') : '',
		},
		{ field: 'periodicity', headerName: 'Periodicity', flex: 1 },
		// { field: 'period_type', headerName: 'Type', flex: 1 },
		{ field: 'source', headerName: 'Source', flex: 1 },
		{
			field: 'override_notes',
			headerName: 'Override Notes',
			flex: 1,
		},
		{
			field: 'delta_days',
			headerName: 'Days',
			flex: 1,
			type: 'number',
		},
		{
			field: 'gap',
			headerName: 'Gap',
			flex: 1,
			type: 'number',
		},
		{
			field: 'yoy_end_delta',
			headerName: 'YoY End Δ',
			flex: 1,
			type: 'number',
		},
	];

	return (
		<Box sx={{ height: 'calc(100vh - 310px)', minHeight: 300 }}>
			<DataGrid
				rows={rows}
				columns={columns}
				loading={isLoading}
				disableSelectionOnClick
				components={{ Toolbar: AdminQuickFilterToolbar }}
				density="compact"
				getCellClassName={(params: GridCellParams) => {
					if (params.field === 'gap' && params.value !== null && params.value !== 0) {
						return 'cell-gap-error';
					}
					return '';
				}}
				sx={{
					'& .cell-gap-error': {
						backgroundColor: 'error.main',
						color: 'error.contrastText',
					},
				}}
			/>
		</Box>
	);
}

